import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'motion/react';
import { Avatar, Stack, useMediaQuery } from '@mui/material';
import SectionLayout from './SectionLayout';
import myAvatar from '../avatar.jpg';

const prefix = '//';
const titles = ['Software Developer', 'Engineer', 'Maker'];

// Glowing sci-fi HUD rings orbiting the avatar: one solid arc and one
// segmented "scanner" ring. dashArray uses pathLength=1 units (fraction of the
// ring), so '0.32 1' is a 32% arc and '0.015 0.038' is a ring of small ticks.
const ARC_COLOR = '#ffffff';
const orbitArcs = [
  { size: 116, width: 0.5, duration: 30, dir: 1, opacity: 0.5, dashArray: '0.14 0.16 0.05 0.18 0.12 0.15 0.03 0.17', glow: 1.2 },
  { size: 124, width: 0.35, duration: 84, dir: -1, opacity: 0.38, dashArray: '0.18 0.22 0.04 0.24 0.10 0.22', glow: 1 },
];

// Physically adjacent keys on a QWERTY keyboard, used to pick believable typos.
const adjacentKeys = {
  q: 'wa', w: 'qeasd', e: 'wrsdf', r: 'etdfg', t: 'ryfgh',
  y: 'tughj', u: 'yihjk', i: 'uojkl', o: 'ipkl', p: 'ol',
  a: 'qwsz', s: 'awedzx', d: 'serfcx', f: 'drtgvc', g: 'ftyhbv',
  h: 'gyujnb', j: 'huikmn', k: 'jiolm', l: 'kop',
  z: 'asx', x: 'zsdc', c: 'xdfv', v: 'cfgb', b: 'vghn', n: 'bhjm', m: 'njk',
};

// A human keystroke interval: a base cadence with random jitter so the
// typing never feels metronomic.
const keyDelay = () => 110 + Math.random() * 140;

// Pick a random key adjacent to `ch`, matching its case. Returns null for
// characters (spaces, symbols) with no defined neighbours.
const typoKeyFor = (ch) => {
  const lower = ch.toLowerCase();
  const neighbors = adjacentKeys[lower];
  if (!neighbors) return null;
  const pick = neighbors[Math.floor(Math.random() * neighbors.length)];
  return ch === lower ? pick : pick.toUpperCase();
};

function HeroSection({ heroRef, onScrollDown, style }) {
  const [typedTagline, setTypedTagline] = useState('');
  const isMobile = useMediaQuery('(max-width:600px)');
  const titleIndexRef = useRef(0);
  const timersRef = useRef([]);
  const animatingRef = useRef(false);
  // Called each time a title finishes typing; the mobile auto-cycle uses it to
  // schedule the next switch a fixed pause after typing completes.
  const onTypedRef = useRef(null);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearInterval);
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  const schedule = useCallback((fn, delay) => {
    const id = setTimeout(fn, delay);
    timersRef.current.push(id);
    return id;
  }, []);

  // Type the given title in, one character at a time, starting from the prefix.
  // Sometimes a lone adjacent-key typo slips in; it's noticed once the word
  // containing it is finished, then backspaced away and retyped correctly
  // before typing carries on into the rest of the title.
  const typeTitle = useCallback((word, typePrefix = false) => {
    animatingRef.current = true;
    const full = prefix + word;

    // Decide up front whether this word gets a typo, and where.
    let typoIndex = -1;
    let typoKey = null;
    if (Math.random() < 0.5) {
      const candidates = [];
      for (let k = prefix.length; k < full.length; k += 1) {
        if (typoKeyFor(full[k])) candidates.push(k);
      }
      if (candidates.length) {
        typoIndex = candidates[Math.floor(Math.random() * candidates.length)];
        typoKey = typoKeyFor(full[typoIndex]);
      }
    }

    // The string typed on the first pass: `full`, but with one wrong
    // (adjacent) key at `typoIndex`.
    const firstPass = typoIndex === -1
      ? full
      : full.slice(0, typoIndex) + typoKey + full.slice(typoIndex + 1);

    // The typo isn't noticed right away — a few more characters get typed
    // after it first, but never past the end of the word it landed in.
    let noticeAt = full.length;
    if (typoIndex !== -1) {
      const nextSpace = full.indexOf(' ', typoIndex);
      const wordEnd = nextSpace === -1 ? full.length : nextSpace;
      const extra = 2 + Math.floor(Math.random() * 3); // 2–4 more characters
      noticeAt = Math.min(typoIndex + 1 + extra, wordEnd);
    }

    // On first load type the `//` prefix out too; otherwise it's already there.
    const startIndex = typePrefix ? 0 : prefix.length;
    setTypedTagline(typePrefix ? '' : prefix);

    // Phase 3: retype from the typo position onward, correctly this time.
    const retype = (i) => {
      if (i >= full.length) {
        animatingRef.current = false;
        onTypedRef.current?.();
        return;
      }
      setTypedTagline(full.slice(0, i + 1));
      schedule(() => retype(i + 1), keyDelay());
    };

    // Phase 2: backspace from the end of the mistyped word down to the typo.
    const backspace = (len) => {
      if (len <= typoIndex) {
        retype(typoIndex);
        return;
      }
      setTypedTagline(firstPass.slice(0, len - 1));
      schedule(() => backspace(len - 1), 90);
    };

    // Phase 1: type left to right until the mistyped word is finished (or the
    // whole title, when there's no typo).
    const type = (i) => {
      if (i >= noticeAt) {
        if (typoIndex === -1) {
          animatingRef.current = false;
          onTypedRef.current?.();
        } else {
          // The mistyped word is on screen — pause, then go fix it.
          schedule(() => backspace(noticeAt), 500);
        }
        return;
      }
      setTypedTagline(firstPass.slice(0, i + 1));
      schedule(() => type(i + 1), keyDelay());
    };

    schedule(() => type(startIndex), keyDelay());
  }, [schedule]);

  // Backspace the current title down to the prefix, then type the next one.
  const cycleTitle = useCallback(() => {
    if (animatingRef.current) return;
    animatingRef.current = true;
    titleIndexRef.current = (titleIndexRef.current + 1) % titles.length;
    const nextWord = titles[titleIndexRef.current];
    let current = prefix + titles[(titleIndexRef.current - 1 + titles.length) % titles.length];
    const deleteId = setInterval(() => {
      current = current.slice(0, -1);
      setTypedTagline(current);
      if (current.length === prefix.length) {
        clearInterval(deleteId);
        typeTitle(nextWord);
      }
    }, 50);
    timersRef.current.push(deleteId);
  }, [typeTitle]);

  useEffect(() => {
    const startId = setTimeout(() => typeTitle(titles[0], true), 600);
    timersRef.current.push(startId);
    return clearTimers;
  }, [typeTitle, clearTimers]);

  // On mobile there's no hover, so cycle automatically — but schedule the next
  // switch a fixed pause *after* the current title finishes typing, so the hold
  // time is consistent (a fixed interval would land mid-typing).
  useEffect(() => {
    if (!isMobile) {
      onTypedRef.current = null;
      return undefined;
    }
    onTypedRef.current = () => {
      const id = setTimeout(cycleTitle, 8000);
      timersRef.current.push(id);
    };
    return () => { onTypedRef.current = null; };
  }, [isMobile, cycleTitle]);

  return (
    <SectionLayout sectionRef={heroRef} style={style} onScrollDown={onScrollDown}>
      <Stack spacing={3} justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
        <h1 style={{ fontSize: '50px', textShadow: '0 0 24px rgba(255,255,255,0.55)' }}>Taryl Brown</h1>
        <code
          onMouseEnter={cycleTitle}
          style={{ marginBottom: '50px', lineHeight: 1.4, minHeight: '1.4em' }}
        >
          {typedTagline}
        </code>
        <motion.div
          whileHover={{ scale: 1.1 }}
          style={{ position: 'relative', display: 'inline-flex' }}
        >
          {orbitArcs.map((arc, i) => (
            <motion.svg
              key={i}
              viewBox="0 0 100 100"
              aria-hidden
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: `${arc.size}%`,
                height: `${arc.size}%`,
                marginTop: `${-arc.size / 2}%`,
                marginLeft: `${-arc.size / 2}%`,
                overflow: 'visible',
                transformOrigin: 'center',
                pointerEvents: 'none',
                filter: `drop-shadow(0 0 ${arc.glow}px rgba(255, 255, 255, 0.8))`,
              }}
              animate={{ rotate: 360 * arc.dir }}
              transition={{ duration: arc.duration, repeat: Infinity, ease: 'linear' }}
            >
              <circle
                cx="50"
                cy="50"
                r="47"
                fill="none"
                stroke={ARC_COLOR}
                strokeWidth={arc.width}
                strokeLinecap="round"
                pathLength="1"
                strokeDasharray={arc.dashArray}
                style={{ opacity: arc.opacity }}
              />
            </motion.svg>
          ))}
          <Avatar alt="Taryl Brown" src={myAvatar} sx={{ width: { xs: '60vw', sm: '40vw', md: '20vw' }, height: 'auto', aspectRatio: '1', display: 'block' }} />
        </motion.div>
      </Stack>
    </SectionLayout>
  );
}

export default HeroSection;
